import { getApiEndpoint } from "../config/config";

export const esignatureService = {
  // Document operations
  async createDocument(formData, jwt) {
    const response = await fetch(getApiEndpoint("/v1/esignature/documents"), {
      method: "POST",
      headers: {
        Authorization: `Bearer ${jwt}`,
      },
      body: formData,
    });

    if (!response.ok) {
      throw new Error("Failed to upload document");
    }

    return await response.json();
  },

  async getDocuments(jwt, status = null) {
    const url = status
      ? getApiEndpoint(`/v1/esignature/documents?status=${status}`)
      : getApiEndpoint("/v1/esignature/documents");

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${jwt}`,
      },
    });

    if (!response.ok) {
      throw new Error("Failed to fetch documents");
    }

    return await response.json();
  },

  async getDocument(documentId, jwt) {
    const response = await fetch(
      getApiEndpoint(`/v1/esignature/documents/${documentId}`),
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error("Failed to fetch document");
    }

    return await response.json();
  },

  async addRecipients(documentId, recipients, jwt) {
    const response = await fetch(
      getApiEndpoint(`/v1/esignature/documents/${documentId}/recipients`),
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({ recipients }),
      }
    );

    if (!response.ok) {
      throw new Error("Failed to add recipients");
    }

    return await response.json();
  },

  async addFields(documentId, fields, jwt) {
    const response = await fetch(
      getApiEndpoint(`/v1/esignature/documents/${documentId}/fields`),
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({ fields }),
      }
    );

    if (!response.ok) {
      throw new Error("Failed to add fields");
    }

    return await response.json();
  },

  async sendDocument(documentId, jwt) {
    const response = await fetch(
      getApiEndpoint(`/v1/esignature/documents/${documentId}/send`),
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error("Failed to send document");
    }

    return await response.json();
  },

  // Public signing operations (no auth required)
  async getDocumentByToken(token) {
    const response = await fetch(
      getApiEndpoint(`/v1/esignature/sign/${token}`),
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      throw new Error("Failed to fetch document");
    }

    return await response.json();
  },

  // Record the signer's electronic-records consent. The backend requires this
  // before any field can be signed.
  async acceptConsent(token) {
    const response = await fetch(
      getApiEndpoint(`/v1/esignature/sign/${token}/consent`),
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ agreed: true }),
      }
    );

    if (!response.ok) {
      throw new Error("Failed to record consent");
    }

    return await response.json();
  },

  async submitSignature(token, fieldId, signatureData) {
    const response = await fetch(
      getApiEndpoint(`/v1/esignature/sign/${token}/field`),
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          field_id: fieldId,
          ...signatureData,
        }),
      }
    );

    if (!response.ok) {
      throw new Error("Failed to submit signature");
    }

    return await response.json();
  },

  async downloadSignedDocument(documentId, jwt) {
    const response = await fetch(
      getApiEndpoint(`/v1/esignature/documents/${documentId}/download`),
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error("Failed to get download link");
    }

    return await response.json();
  },

  // Template operations
  async createTemplate(formData, jwt) {
    const response = await fetch(getApiEndpoint("/v1/esignature/templates"), {
      method: "POST",
      headers: {
        Authorization: `Bearer ${jwt}`,
      },
      body: formData,
    });

    if (!response.ok) {
      throw new Error("Failed to create template");
    }

    return await response.json();
  },

  async getTemplates(jwt) {
    const response = await fetch(getApiEndpoint("/v1/esignature/templates"), {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${jwt}`,
      },
    });

    if (!response.ok) {
      throw new Error("Failed to fetch templates");
    }

    return await response.json();
  },

  async getAvailableTemplates(jwt) {
    const response = await fetch(
      getApiEndpoint("/v1/esignature/templates/available"),
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error("Failed to fetch available templates");
    }

    return await response.json();
  },

  async getTemplate(templateId, jwt) {
    const response = await fetch(
      getApiEndpoint(`/v1/esignature/templates/${templateId}`),
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error("Failed to fetch template");
    }

    return await response.json();
  },

  async addFieldsToTemplate(templateId, fields, jwt) {
    const response = await fetch(
      getApiEndpoint(`/v1/esignature/templates/${templateId}/fields`),
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({ fields }),
      }
    );

    if (!response.ok) {
      throw new Error("Failed to add fields to template");
    }

    return await response.json();
  },

  async createDocumentFromTemplate(templateId, documentData, jwt) {
    const response = await fetch(
      getApiEndpoint(`/v1/esignature/templates/${templateId}/use`),
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify(documentData),
      }
    );

    if (!response.ok) {
      throw new Error("Failed to create document from template");
    }

    return await response.json();
  },

  async updateTemplate(templateId, templateData, jwt) {
    const response = await fetch(
      getApiEndpoint(`/v1/esignature/templates/${templateId}`),
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify(templateData),
      }
    );

    if (!response.ok) {
      throw new Error("Failed to update template");
    }

    return await response.json();
  },

  async deleteTemplate(templateId, jwt) {
    const response = await fetch(
      getApiEndpoint(`/v1/esignature/templates/${templateId}/delete`),
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error("Failed to delete template");
    }

    return await response.json();
  },

  async deleteDocument(documentId, jwt) {
    const response = await fetch(
      getApiEndpoint(`/v1/esignature/documents/${documentId}/delete`),
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error("Failed to delete document");
    }

    return await response.json();
  },
};
